import React, { useEffect, useState } from 'react';
import { useAuth, Profile } from '../context/AuthContext';
import { adminService } from '../../services/adminService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';

const Dashboard: React.FC = () => {
  const { profile } = useAuth();
  const isSuperadmin = profile?.role === 'superadmin';

  const [patients, setPatients] = useState<Profile[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  
  // Selected patient for viewing history
  const [selectedPatient, setSelectedPatient] = useState<Profile | null>(null);
  const [patientHistory, setPatientHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Superadmin: Doctor management
  const [doctors, setDoctors] = useState<(Profile & { assigned_places: string[] })[]>([]);
  const [controlPlaces, setControlPlaces] = useState<string[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<string | null>(null);
  const [selectedControlPlace, setSelectedControlPlace] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Add new doctor
  const [addDoctorOpen, setAddDoctorOpen] = useState(false);
  const [newDoctorName, setNewDoctorName] = useState('');
  const [newDoctorEmail, setNewDoctorEmail] = useState('');
  const [newDoctorPassword, setNewDoctorPassword] = useState('');
  const [addDoctorError, setAddDoctorError] = useState<string | null>(null);
  const [isCreatingDoctor, setIsCreatingDoctor] = useState(false);

  useEffect(() => {
    if (profile) {
      loadPatients();
      if (isSuperadmin) {
        loadDoctorsAndPlaces();
      }
    }
  }, [profile]);

  const loadPatients = async () => {
    setLoadingPatients(true);
    try {
      if (isSuperadmin) {
        const data = await adminService.getAllPatients();
        setPatients(data);
      } else {
        const data = await adminService.getPatientsForDoctor(profile!.id);
        setPatients(data);
      }
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadDoctorsAndPlaces = async () => {
    setLoadingDoctors(true);
    try {
      const [docs, places] = await Promise.all([
        adminService.getDoctors(),
        adminService.getDistinctControlPlaces()
      ]);
      setDoctors(docs);
      setControlPlaces(places);
    } catch (error) {
      console.error('Error loading doctors & places:', error);
    } finally {
      setLoadingDoctors(false);
    }
  };

  const handleSelectPatient = async (patient: Profile) => {
    setSelectedPatient(patient);
    setLoadingHistory(true);
    try {
      const history = await adminService.getPatientHistory(patient.id);
      setPatientHistory(history);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleAssignDoctor = async () => {
    if (!selectedDoctor || !selectedControlPlace) return;
    setIsAssigning(true);
    try {
      await adminService.assignDoctorToControlPlace(selectedDoctor, selectedControlPlace);
      await loadDoctorsAndPlaces();
      setSelectedControlPlace('');
    } catch (error) {
      console.error('Error assigning doctor:', error);
      alert('Gagal assign dokter. Mungkin sudah di-assign ke tempat ini.');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveDoctor = async (doctorId: string, controlPlace: string) => {
    if (!window.confirm('Hapus kontrol place ini dari dokter?')) return;
    try {
      await adminService.removeDoctorFromControlPlace(doctorId, controlPlace);
      await loadDoctorsAndPlaces();
    } catch (error) {
      console.error('Error removing doctor control place:', error);
    }
  };

  const handleAddDoctor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDoctorName.trim() || !newDoctorEmail.trim() || !newDoctorPassword.trim()) return;
    setIsCreatingDoctor(true);
    setAddDoctorError(null);
    try {
      await adminService.createDoctorUser(newDoctorEmail.trim(), newDoctorPassword, newDoctorName.trim());
      await loadDoctorsAndPlaces();
      setAddDoctorOpen(false);
      setNewDoctorName('');
      setNewDoctorEmail('');
      setNewDoctorPassword('');
    } catch (err: any) {
      setAddDoctorError(err.message || 'Gagal membuat akun dokter.');
    } finally {
      setIsCreatingDoctor(false);
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-8 space-y-6">
      <div className="flex flex-col space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Selamat datang, {profile?.full_name} ({profile?.role})
        </p>
      </div>

      <Tabs defaultValue="patients" className="space-y-4">
        <TabsList>
          <TabsTrigger value="patients">Pasien</TabsTrigger>
          {isSuperadmin && <TabsTrigger value="doctors">Manajemen Dokter</TabsTrigger>}
        </TabsList>

        <TabsContent value="patients" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Patients List */}
            <Card className="col-span-1 md:col-span-1">
              <CardHeader>
                <CardTitle>Daftar Pasien</CardTitle>
                <CardDescription>
                  {isSuperadmin ? 'Semua pasien yang terdaftar' : 'Pasien di bawah pengawasan Anda'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingPatients ? (
                  <div className="text-sm">Memuat pasien...</div>
                ) : patients.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Tidak ada pasien.</div>
                ) : (
                  <ScrollArea className="h-[500px] w-full pr-4">
                    <div className="space-y-2">
                      {patients.map((p) => (
                        <Button
                          key={p.id}
                          variant={selectedPatient?.id === p.id ? "default" : "outline"}
                          className="w-full justify-start text-left h-auto py-3"
                          onClick={() => handleSelectPatient(p)}
                        >
                          <div className="flex flex-col items-start gap-1 w-full overflow-hidden">
                            <span className="font-semibold truncate w-full">{p.full_name}</span>
                            <div className="flex justify-between w-full text-xs opacity-80">
                              <span>{p.diabetes_type}</span>
                              <span className="truncate max-w-[50%]">{p.control_place}</span>
                            </div>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Patient Detail & History */}
            <Card className="col-span-1 md:col-span-2">
              <CardHeader>
                <CardTitle>
                  {selectedPatient ? `Riwayat: ${selectedPatient.full_name}` : 'Pilih pasien untuk melihat riwayat'}
                </CardTitle>
                {selectedPatient && (
                  <CardDescription>
                    {selectedPatient.diabetes_type} | Tempat Kontrol: {selectedPatient.control_place}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {!selectedPatient ? (
                  <div className="flex justify-center items-center h-48 text-muted-foreground">
                    Belum ada pasien yang dipilih.
                  </div>
                ) : loadingHistory ? (
                  <div className="text-sm">Memuat riwayat...</div>
                ) : patientHistory.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Belum ada catatan riwayat untuk pasien ini.</div>
                ) : (
                  <ScrollArea className="h-[450px] rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tanggal</TableHead>
                          <TableHead>Waktu</TableHead>
                          <TableHead>Gula Darah</TableHead>
                          <TableHead>Dosis Insulin</TableHead>
                          <TableHead>Jenis/Brand</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {patientHistory.map((h) => (
                          <TableRow key={h.id}>
                            <TableCell>{h.date}</TableCell>
                            <TableCell>{h.time?.slice(0, 5)}</TableCell>
                            <TableCell>
                              {h.glucose_value ? (
                                <Badge variant={h.glucose_value > 140 ? 'destructive' : 'secondary'}>
                                  {h.glucose_value} mg/dL
                                </Badge>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {h.insulin_dose ? `${h.insulin_dose} unit` : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col text-xs">
                                <span>{h.insulin_type_id?.replace('type-', '')}</span>
                                <span className="text-muted-foreground">{h.insulin_brand}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {isSuperadmin && (
          <TabsContent value="doctors" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Daftar Dokter</CardTitle>
                    <CardDescription className="mt-1">
                      Kelola penugasan tempat kontrol untuk setiap dokter dari tempat kontrol pasien yang terdaftar.
                    </CardDescription>
                  </div>

                  {/* Add New Doctor Dialog */}
                  <Dialog open={addDoctorOpen} onOpenChange={(open) => {
                    setAddDoctorOpen(open);
                    if (!open) { setAddDoctorError(null); }
                  }}>
                    <DialogTrigger asChild>
                      <Button>+ Tambah Dokter</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Tambah Dokter Baru</DialogTitle>
                        <DialogDescription>
                          Buat akun pengguna baru dengan role dokter. Dokter dapat login menggunakan email dan password yang diisi.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleAddDoctor} className="space-y-4 py-2">
                        <div className="space-y-1">
                          <label className="text-sm font-medium" htmlFor="doctor-name">Nama Lengkap</label>
                          <input
                            id="doctor-name"
                            type="text"
                            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            placeholder="dr. John Doe"
                            value={newDoctorName}
                            onChange={(e) => setNewDoctorName(e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-sm font-medium" htmlFor="doctor-email">Email</label>
                          <input
                            id="doctor-email"
                            type="email"
                            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            placeholder="dokter@email.com"
                            value={newDoctorEmail}
                            onChange={(e) => setNewDoctorEmail(e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-sm font-medium" htmlFor="doctor-password">Password</label>
                          <input
                            id="doctor-password"
                            type="password"
                            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            placeholder="Minimal 6 karakter"
                            minLength={6}
                            value={newDoctorPassword}
                            onChange={(e) => setNewDoctorPassword(e.target.value)}
                            required
                          />
                        </div>

                        {addDoctorError && (
                          <p className="text-sm text-destructive">{addDoctorError}</p>
                        )}

                        <Separator />
                        <Button type="submit" className="w-full" disabled={isCreatingDoctor}>
                          {isCreatingDoctor ? 'Membuat akun...' : 'Buat Akun Dokter'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {loadingDoctors ? (
                  <div>Memuat data dokter...</div>
                ) : doctors.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                    <p className="text-sm">Belum ada dokter yang terdaftar.</p>
                    <p className="text-xs">Klik "+ Tambah Dokter" untuk menambahkan dokter baru.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nama Dokter</TableHead>
                        <TableHead>Tempat Kontrol (Ditugaskan)</TableHead>
                        <TableHead className="text-right">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {doctors.map((doc) => (
                        <TableRow key={doc.id}>
                          <TableCell className="font-medium">{doc.full_name || 'Tanpa Nama'}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {doc.assigned_places.length > 0 ? (
                                doc.assigned_places.map(place => (
                                  <Badge key={place} variant="secondary" className="flex items-center gap-1">
                                    {place}
                                    <button 
                                      onClick={() => handleRemoveDoctor(doc.id, place)}
                                      className="text-xs hover:text-destructive ml-1"
                                      title="Hapus tempat kontrol"
                                    >
                                      ×
                                    </button>
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-sm text-muted-foreground">Belum ada penugasan</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm" onClick={() => setSelectedDoctor(doc.id)}>
                                  Assign
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Assign Tempat Kontrol</DialogTitle>
                                  <DialogDescription>
                                    Pilih tempat kontrol dari daftar di bawah ini untuk ditugaskan ke {doc.full_name}.
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <Select value={selectedControlPlace} onValueChange={setSelectedControlPlace}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Pilih tempat kontrol" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {controlPlaces.map(place => (
                                        <SelectItem key={place} value={place}>
                                          {place}
                                        </SelectItem>
                                      ))}
                                      {controlPlaces.length === 0 && (
                                        <SelectItem value="none" disabled>Tidak ada tempat kontrol terdaftar</SelectItem>
                                      )}
                                    </SelectContent>
                                  </Select>
                                  <Button 
                                    className="w-full" 
                                    onClick={handleAssignDoctor} 
                                    disabled={!selectedControlPlace || isAssigning}
                                  >
                                    {isAssigning ? 'Menyimpan...' : 'Simpan Penugasan'}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Dashboard;
